import { useCallback, useEffect, useRef, useState } from 'react';
import { trackMixpanelEvent } from '../../../utils/general';
import { setPendingPrompt } from './askSingularAIHandoff';
import { ANALYSIS_CHAT_HASH, MIXPANEL_PREFIX, STARTING_DELAY_MS } from './consts';

export const VIEW = { CLOSED: 'closed', OPEN: 'open', STARTING: 'starting' };

const useAskSingularAI = () => {
    const [view, setView] = useState(VIEW.CLOSED);
    const [prompt, setPrompt] = useState('');
    const promptRef = useRef('');
    const usedSuggestionRef = useRef(false);
    const suggestionMetaRef = useRef(null);
    const viewRef = useRef(VIEW.CLOSED);
    const navTimerRef = useRef(null);

    useEffect(() => {
        viewRef.current = view;
    }, [view]);

    const reset = useCallback(() => {
        promptRef.current = '';
        usedSuggestionRef.current = false;
        suggestionMetaRef.current = null;
        setPrompt('');
    }, []);

    const open = useCallback(() => {
        reset();
        setView(VIEW.OPEN);
        trackMixpanelEvent(MIXPANEL_PREFIX, 'Opened', {});
    }, [reset]);

    const close = useCallback(() => {
        clearTimeout(navTimerRef.current);
        reset();
        setView(VIEW.CLOSED);
    }, [reset]);

    const onType = useCallback(value => {
        usedSuggestionRef.current = false;
        suggestionMetaRef.current = null;
        promptRef.current = value;
        setPrompt(value);
    }, []);

    const pickSuggestion = useCallback((text, meta = null) => {
        usedSuggestionRef.current = true;
        suggestionMetaRef.current = meta;
        promptRef.current = text;
        setPrompt(text);
    }, []);

    const submit = useCallback(({ usedVoice = false } = {}) => {
        if (viewRef.current === VIEW.STARTING) return;
        const trimmed = promptRef.current.trim();
        if (!trimmed) return;
        const usedSuggestion = usedSuggestionRef.current;
        const suggestionMeta = suggestionMetaRef.current;
        setView(VIEW.STARTING);
        clearTimeout(navTimerRef.current);
        navTimerRef.current = setTimeout(() => {
            trackMixpanelEvent(MIXPANEL_PREFIX, 'Submitted', {
                promptLength: trimmed.length,
                usedSuggestion,
                usedVoice,
                ...(suggestionMeta || {}),
            });
            setPendingPrompt(trimmed, { usedVoice });
            window.location.hash = ANALYSIS_CHAT_HASH;
            promptRef.current = '';
            usedSuggestionRef.current = false;
            suggestionMetaRef.current = null;
            setPrompt('');
            setView(VIEW.CLOSED);
        }, STARTING_DELAY_MS);
    }, []);

    useEffect(() => {
        const onKeyDown = e => {
            if (e.ctrlKey && e.key.toLowerCase() === 'k') {
                e.preventDefault();
                if (viewRef.current === VIEW.CLOSED) open();
                else close();
            } else if (e.key === 'Escape' && viewRef.current !== VIEW.CLOSED) {
                close();
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [open, close]);

    useEffect(() => () => clearTimeout(navTimerRef.current), []);

    return {
        view,
        isOpen: view !== VIEW.CLOSED,
        isStarting: view === VIEW.STARTING,
        prompt,
        onType,
        pickSuggestion,
        submit,
        open,
        close,
    };
};

export default useAskSingularAI;
